import { Button, Typography } from '@scale/scaleui';
import { useEffect, useContext } from 'react';
import { useTrans } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useTrans';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { IndexedResponse, ModelResponseSelectorStepResponse } from '@scale/shared/types/chat';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import isNil from 'lodash/isNil';
import Editor from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/Editor';
import EditorContainer from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/EditorContainer';
import { createMention } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/plugins/mentions';
import PlainTextEditorContext from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/PlainTextEditorContext';
import JustificationSuggestionsContainer from 'react-scale/components/scalers/tasks/ThreadedInteraction/ResponseSuggestions/components/JustificationSuggestionsContainer';
import { AutoraterResponse } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ResponseSuggestions/hooks/useResponseSuggestions';

export const JustificationEditor = (props: {
  value: string;
  setValue: (value: string) => void;
  onSubmit: (value: string) => void;
  mentionCandidates?: { name: string; id: string; subtext?: string }[];
  response: IndexedResponse<ModelResponseSelectorStepResponse> | undefined;
  readOnly: boolean;
  complete?: boolean;
  suggestions?: AutoraterResponse;
  suggestionsLoading?: boolean;
}) => {
  const { t } = useTrans();
  const {
    value,
    setValue,
    onSubmit,
    mentionCandidates,
    response,
    readOnly,
    complete,
    suggestions,
    suggestionsLoading,
  } = props;
  const taskContext = useContext(ChatTaskContext);

  const disablePasting = taskContext?.disableResponsePasting || false;

  useEffect(() => {
    return () => {
      const element = document.querySelector('.suggestion-item-list');
      if (element) {
        element.remove();
      }
    };
  }, []);

  const markStepAsIncompletedByIndex = useChatStoreExtract(
    state => state.markStepAsIncompletedByIndex,
  );

  const handleChange = (newValue: string) => {
    setValue(newValue);

    if (
      response &&
      !isNil(response.index) &&
      complete &&
      newValue !== response.context?.justificationRaw
    ) {
      markStepAsIncompletedByIndex(response.index);
    }
  };

  return (
    <div className="flex flex-col gap-3 mt-6">
      <Typography.Subtitle3 className="flex items-center gap-2">
        Please provide a justification. Use{' '}
        {mentionCandidates && mentionCandidates.length > 2
          ? mentionCandidates.map((mention, index) => {
              if (index === mentionCandidates.length - 1) {
                return `and "@${mention.name}"`;
              }
              return `"@${mention.name}", `;
            })
          : `"@Response 1" and "@Response 2"`}{' '}
        to refer to the model responses.
      </Typography.Subtitle3>{' '}
      {(!!suggestions?.justification || suggestionsLoading) && (
        <JustificationSuggestionsContainer
          justification={suggestions?.justification}
          loading={suggestionsLoading}
        />
      )}
      <PlainTextEditorContext
        initialContent={value}
        extensions={[
          createMention({
            items: mentionCandidates?.length
              ? mentionCandidates.map(candidate => ({ id: candidate.id, label: candidate.name }))
              : [
                  { id: '1', label: 'Response 1' },
                  { id: '2', label: 'Response 2' },
                ],
          }),
        ]}
      >
        <EditorContainer>
          <Editor
            value={value}
            onContentChange={handleChange}
            readOnly={readOnly}
            disablePaste={disablePasting}
          />
        </EditorContainer>
      </PlainTextEditorContext>
      {!taskContext.previewMode && (
        <div className="flex flex-row justify-end">
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              onSubmit(value);
            }}
            disabled={
              complete &&
              (value === response?.context?.justificationRaw || !value?.length || readOnly)
            }
            data-testid="justification-submit-button"
          >
            {t('save-and-continue')}
          </Button>
        </div>
      )}
    </div>
  );
};
